import { definePreset } from '@primeuix/styled';
import Aura from '@primevue/themes/aura';

const primary = {
  50: '#f5f9ff',
  100: '#d0e1fd',
  200: '#abc9fb',
  300: '#85b2f9',
  400: '#609af8',
  500: '#3b82f6',
  600: '#326fd1',
  700: '#295bac',
  800: '#204887',
  900: '#183462',
  950: '#10254b',
};

const surface = {
  0: '#ffffff',
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
  950: '#020617',
};

const text = {
  color: '#334155',
  hoverColor: '#1e293b',
  mutedColor: '#64748b',
  hoverMutedColor: '#475569',
};

const highlight = {
  background: '#eff6ff',
  focusBackground: '#dbeafe',
  color: '#1d4ed8',
  focusColor: '#1d4ed8',
};

const overlay = {
  select: {
    background: surface[0],
    borderColor: surface[200],
    color: text.color,
    borderRadius: '6px',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  },
  popover: {
    background: surface[0],
    borderColor: surface[200],
    color: text.color,
    borderRadius: '6px',
    padding: '0.75rem',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  },
  modal: {
    background: surface[0],
    borderColor: surface[200],
    color: text.color,
    borderRadius: '12px',
    padding: '1.25rem',
    shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  },
  navigation: {
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  },
};

const accordion = {
  panel: {
    borderWidth: '0',
    borderColor: 'transparent',
  },
  header: {
    padding: '1rem 0',
    borderWidth: '0',
    borderColor: 'transparent',
    background: 'transparent',
    hoverBackground: 'transparent',
    activeBackground: 'transparent',
    activeHoverBackground: 'transparent',
    color: text.color,
    hoverColor: text.color,
    activeColor: text.color,
    activeHoverColor: text.color,
    toggleIcon: {
      color: text.mutedColor,
      hoverColor: text.color,
      activeColor: text.color,
      activeHoverColor: text.color,
    },
    focusRing: {
      width: '0px',
      style: 'solid',
      color: 'transparent',
      offset: '0px',
      shadow: 'none',
    },
    first: {
      topBorderRadius: '0',
      borderWidth: '0',
    },
    last: {
      bottomBorderRadius: '0',
      activeBottomBorderRadius: '0',
    },
  },
  content: {
    borderWidth: '0',
    borderColor: 'transparent',
    background: 'transparent',
    color: text.color,
    padding: '0 0 1rem 0',
  },
};

const radiobutton = {
  root: {
    width: '20px',
    height: '20px',
    background: surface[0],
    checkedBackground: surface[0],
    checkedHoverBackground: surface[0],
    disabledBackground: surface[100],
    filledBackground: surface[50],
    borderColor: surface[300],
    hoverBorderColor: primary[500],
    focusBorderColor: primary[500],
    checkedBorderColor: primary[500],
    checkedHoverBorderColor: primary[600],
    checkedFocusBorderColor: primary[500],
    checkedDisabledBorderColor: surface[300],
    invalidBorderColor: '#f87171',
    shadow: 'none',
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'none',
    },
    transitionDuration: '0.2s',
  },
  icon: {
    size: '10px',
    checkedColor: primary[500],
    checkedHoverColor: primary[600],
    disabledColor: surface[400],
  },
};

const checkbox = {
  root: {
    width: '20px',
    height: '20px',
    background: surface[0],
    checkedBackground: primary[500],
    checkedHoverBackground: primary[600],
    disabledBackground: surface[100],
    filledBackground: surface[50],
    borderColor: surface[500],
    hoverBorderColor: primary[500],
    focusBorderColor: primary[500],
    checkedBorderColor: primary[500],
    checkedHoverBorderColor: primary[600],
    checkedFocusBorderColor: primary[500],
    checkedDisabledBorderColor: surface[300],
    invalidBorderColor: '#f87171',
    shadow: 'none',
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'none',
    },
    borderRadius: '4px',
    transitionDuration: '0.2s',
  },
  icon: {
    size: '12px',
    color: surface[0],
    checkedColor: '#ffffff',
    checkedHoverColor: '#ffffff',
    disabledColor: surface[400],
  },
};

const tripeningTheme = definePreset(Aura, {
  primitive: {
    emerald: primary,
    slate: surface,
  },
  semantic: {
    focusRing: {
      width: '0px',
      style: 'solid',
      color: primary[500],
      offset: '0px',
      shadow: '0 0 0 2px rgba(59, 130, 246, 0.25)',
    },
    anchorGutter: '2px',
    overlay,
    colorScheme: {
      light: {
        surface,
        primary: {
          color: primary[500],
          contrastColor: '#ffffff',
          hoverColor: primary[600],
          activeColor: primary[700],
        },
        highlight,
        mask: {
          background: 'rgba(0,0,0,0.4)',
          color: surface[200],
        },
        formField: {
          background: surface[0],
          disabledBackground: surface[100],
          filledBackground: surface[50],
          filledHoverBackground: surface[100],
          filledFocusBackground: surface[0],
          borderColor: surface[200],
          hoverBorderColor: surface[300],
          focusBorderColor: primary[500],
          invalidBorderColor: '#f87171',
          color: text.color,
          disabledColor: surface[400],
          placeholderColor: text.mutedColor,
          invalidPlaceholderColor: '#dc2626',
          floatLabelColor: text.mutedColor,
          floatLabelFocusColor: primary[500],
          floatLabelActiveColor: text.color,
          floatLabelInvalidColor: '#dc2626',
          iconColor: surface[400],
          shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
        },
        text,
        content: {
          background: surface[0],
          hoverBackground: surface[100],
          borderColor: surface[200],
          color: text.color,
          hoverColor: text.hoverColor,
        },
        overlay: {
          select: {
            background: surface[0],
            borderColor: surface[200],
            color: text.color,
          },
          popover: {
            background: surface[0],
            borderColor: surface[200],
            color: text.color,
          },
          modal: {
            background: surface[0],
            borderColor: surface[200],
            color: text.color,
          },
        },
        list: {
          option: {
            focusBackground: surface[100],
            selectedBackground: highlight.background,
            selectedFocusBackground: highlight.focusBackground,
            color: text.color,
            focusColor: text.hoverColor,
            selectedColor: highlight.color,
            selectedFocusColor: highlight.focusColor,
            icon: {
              color: surface[400],
              focusColor: surface[500],
            },
          },
          optionGroup: {
            background: 'transparent',
            color: text.mutedColor,
          },
        },
        navigation: {
          item: {
            focusBackground: surface[100],
            activeBackground: surface[100],
            color: text.color,
            focusColor: text.hoverColor,
            activeColor: text.hoverColor,
            icon: {
              color: surface[400],
              focusColor: surface[500],
              activeColor: surface[500],
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: text.mutedColor,
          },
          submenuIcon: {
            color: surface[400],
            focusColor: surface[500],
            activeColor: surface[500],
          },
        },
      },
    },
  },
  components: {
    accordion,
    radiobutton,
    checkbox,
  },
});

export default {
  preset: tripeningTheme,
  options: {
    darkModeSelector: '[data-theme="dark"]',
  },
};
